import { useCallback } from 'react';
import { DateTime } from 'luxon';

import { MAX_RESOURCE_PER_TYPE } from 'src/data/common';
import { FEATURE_LIMIT_CHECK_SOURCE } from 'src/data/featureLimits';
import { isNonEnglish } from 'src/data/generation';
import {
  doesToolSupportBackgroundUrlBriefExtraction,
  doesToolSupportCallToAction,
  isDataDrivenGenerationTool,
  isFreestyleGenerationTool,
  isMultiPromptTool,
} from 'src/data/generationTool';
import { GENERATION_TOOL } from 'src/data/generationTool/const';
import { RESOURCE_CONTENT_TYPE } from 'src/data/performance';
import { SUGGEST_PROMPT_TYPES } from 'src/data/suggestPrompt';
import { promisedWait } from 'src/lib/common';
import {
  PAGES,
  useAddResource,
  useErrorHandler,
  useNotifyAboutGenerationResult,
} from 'src/services';
import { useCustomerActions, useNotificationActions } from 'src/store';
import { extractUrls } from 'src/utils/extractUrl';
import { useQuotaEndedPopup } from 'src/views/QuotaEndedPopup';

import {
  useDocumentViewActions,
  useGenerationActions,
  useListViewActions,
  useMainActions,
} from '../../store';

import { CANVAS_PROJECT_FORM_EVENT } from './const';
import {
  useCollectRecomposeParams,
  useCollectSuggestParams,
  useFormEventHandler,
} from './services';
import { useLocalActions } from './store';
import useEvents from './useEvents';

const useCheckIfIsNonEnglishWithKeywords = () => {
  const localActions = useLocalActions();
  const notificationActions = useNotificationActions();

  return useCallback(() => {
    if (isMultiPromptTool(localActions.getGenerationTool())) {
      // multi prompt tools don't actually generate on this stage yet, so no need for this validation
      return false;
    }

    const hasRegularKeywordsAnnotations = localActions.hasRegularKeywordsAnnotations();
    const language = localActions.getLanguage();

    if (hasRegularKeywordsAnnotations && isNonEnglish(language)) {
      notificationActions.displayMessage(
        'Talking points are not supported for non-English languages. Remove them and try again.'
      );

      return true;
    }

    return false;
  }, [localActions, notificationActions]);
};

export const useSubmitHandler = () => {
  const formEventHandler = useFormEventHandler();
  const showQuotaEndedPopup = useQuotaEndedPopup();
  const notificationActions = useNotificationActions();
  const events = useEvents();

  const handleError = useErrorHandler();
  const customerActions = useCustomerActions();
  const localActions = useLocalActions();
  const mainActions = useMainActions();
  const generationActions = useGenerationActions();
  const documentViewActions = useDocumentViewActions();
  const listViewActions = useListViewActions();

  const notifyAboutGenerationResult = useNotifyAboutGenerationResult();
  const collectRecomposeParams = useCollectRecomposeParams(true);
  const collectSuggestParams = useCollectSuggestParams();
  const checkIfIsNonEnglishWithKeywords = useCheckIfIsNonEnglishWithKeywords();

  const onAddResource = useAddResource({
    page: PAGES.editor,
    origin: 'prompt',
  });

  const generateRecompose = useCallback(async () => {
    // Collect generation params from the form
    const params = collectRecomposeParams();
    const result = await documentViewActions.rephraseTexts(params);
    events.trackRecompose({
      campaignKeywords: params.annotations,
      tovInput: params.tovInput,
    });
    return result;
  }, [collectRecomposeParams, documentViewActions, events]);

  const generateSuggest = useCallback(async () => {
    // Collect generation params from the form
    const params = collectSuggestParams();
    const promptURLs = localActions.getUrlForGeneration();

    const allText = isMultiPromptTool(params.generationTool)
      ? Object.values(params.promptFields).reduce(
          (a, c) => a + c.freestyleToolAllInstructionsData.text,
          ''
        )
      : params.freestyleToolAllInstructions;

    if (extractUrls(allText, MAX_RESOURCE_PER_TYPE)?.overLimit) {
      notificationActions.displayMessage(
        `You can use a maximum of ${MAX_RESOURCE_PER_TYPE} ${RESOURCE_CONTENT_TYPE.url.toUpperCase()}s`
      );
      return;
    }

    const waitForImageUpload = async () => {
      const isUploadingImage = localActions.getIsUploadingImage();
      if (isUploadingImage) {
        await promisedWait(500);
        return waitForImageUpload();
      }
    };

    await waitForImageUpload();
    const imageUploadErrors = localActions.getPromptImageErrors();
    if (Object.keys(imageUploadErrors).length > 0) {
      return;
    }

    if (doesToolSupportCallToAction(params.generationTool)) {
      events.trackGenerateWithCallToAction(params.generationTool);
    }

    if (isFreestyleGenerationTool(params.generationTool)) {
      const isPersonalizedPrompt = localActions.getIsPersonalizedPrompt();
      const isImprovedPrompt = localActions.getIsImprovedPrompt();
      if (isPersonalizedPrompt || isImprovedPrompt) {
        events.trackGenerateWithSuggestedPrompt(
          isPersonalizedPrompt ? SUGGEST_PROMPT_TYPES.personalize : SUGGEST_PROMPT_TYPES.improve
        );
      }
    }

    let briefExtractionPromise;
    if (doesToolSupportBackgroundUrlBriefExtraction(params.generationTool)) {
      briefExtractionPromise = localActions.shouldWaitForBriefExtraction(promptURLs);
    }
    const result = await documentViewActions.suggestVariations(params, { briefExtractionPromise });

    if (promptURLs.length > 0) {
      onAddResource(promptURLs);
    }

    const annotations = localActions.getAnnotationsForGeneration();

    if (isMultiPromptTool(params.generationTool)) {
      // this isn't generated yet, so it's a different event
      events.trackMultiPromptSetup({
        personaForGeneration: params.customerPersona,
      });
    } else {
      events.trackSuggest({
        annotations,
        url: params.url,
        personaForGeneration: params.customerPersona,
        tovInput: params.tovInput,
        customModelId: params.generationCustomModelId,
      });
    }

    return result;
  }, [
    collectSuggestParams,
    documentViewActions,
    localActions,
    events,
    onAddResource,
    notificationActions,
  ]);

  /**
   * Submit generation handler
   */
  return useCallback(async () => {
    const generationTool = localActions.getGenerationTool();

    // Check customer quota
    if (customerActions.isCurrentQuotaEnded()) {
      showQuotaEndedPopup();
      return;
    }

    // Validate "brief" and other generation params
    if (!localActions.validateParams()) {
      return;
    }

    // If there was an unfinished annotation, try to save it - cancel generation in case of validation error
    if (!localActions.completePendingAnnotation()) {
      return;
    }

    if (checkIfIsNonEnglishWithKeywords()) {
      return;
    }

    try {
      if (!localActions.validateForGeneration()) {
        return;
      }

      // Notify outside listener that we are starting generation
      formEventHandler?.(CANVAS_PROJECT_FORM_EVENT.generationStarted);

      mainActions.activateListView(); // Switch to the "list view" - as UI might be in the "document" mode

      // Generate variations
      const result = localActions.isShouldRewrite()
        ? await generateRecompose()
        : await generateSuggest();

      // If nothing was generated, return early - we might have an error in "image uploading" flow
      if (!result) {
        return;
      }

      // Refetch customer quota data and limits, so we can disable generation next time if it's ended
      customerActions.refreshCurrentCustomerQuota();
      customerActions.refreshCurrentCustomerFeatureLimit(FEATURE_LIMIT_CHECK_SOURCE.generationFlow);

      const improvingScore =
        localActions.isShouldRewrite() &&
        localActions.getIsOnlyImproveScore() &&
        generationTool !== GENERATION_TOOL.basicContentImprover;

      if (isMultiPromptTool(generationTool)) {
        return result;
      }

      // Display toast notification for user if needed
      notifyAboutGenerationResult({
        result,
        requestedCount: localActions.getNumberOfVariations(),
        improvingScore,
      });

      const briefErrorBlocking = result?.briefExtractionResult?.blockingErrors;
      if (briefErrorBlocking) {
        return;
      }

      // Push received generation result to the "list view" store
      // Add generation result to the "list" view
      // Set request for "quality survey" and "list" scrolling
      if (Array.isArray(result)) {
        listViewActions.addGenerationResults(result);
        generationActions.setLastGenerationResult(result[0]);
      } else {
        listViewActions.addGenerationResult(result);
        generationActions.setLastGenerationResult(result);
      }

      if (result?.variations && result?.variations?.length === 0) {
        events.trackVariationZeroResult(result);
      }

      // Store "last generation timestamp" - required for "score" onboarding hint
      if (isDataDrivenGenerationTool(generationTool)) {
        listViewActions.setLastGeneratedTimestamp(DateTime.local().toSeconds());
      }
      mainActions.activateListView();
    } catch (error) {
      handleError(error);
    }
  }, [
    localActions,
    customerActions,
    checkIfIsNonEnglishWithKeywords,
    showQuotaEndedPopup,
    mainActions,
    generateRecompose,
    generateSuggest,
    notifyAboutGenerationResult,
    listViewActions,
    generationActions,
    handleError,
    formEventHandler,
    events,
  ]);
};
